import React from 'react'
import PropTypes from 'prop-types'
import { Typography, Box } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import validateDisplayRules, { validateDisplayRulesCustomer } from 'functions/validateDisplayRules'

const ReviewSummary = ({ steps, formData, customer }) => {
  const classes = useStyles()

  // Função para verificar se um campo está preenchido
  const isFieldFilled = (field, data) => {
    const value = data[field.id] || data[field.tag]
        
    // Para campos IMC
    if (field.type === 'imc' || field.type === 'imc_gestacional') {
      const height = data['height']
      const weight = data['weight']
      return height && weight && height !== '' && weight !== ''
    }
    
    if (value === null || value === undefined || value === '') {
      return false
    }
    
    // Para campos de array (multiselect, checkbox)
    if (Array.isArray(value) && value.length === 0) {
      return false
    }

    return true
  }

  // Calcular estatísticas
  const calculateStats = () => {
    let totalFields = 0
    let filledFields = 0
    const unfilledFields = []

    steps.forEach(step => {
      step.fields.forEach(field => {
        // Pular campos que não devem ser contados
        if (field.type === 'label') {
          return
        }

        // Verificar display rules
        if (!validateDisplayRules(formData, field) || !validateDisplayRulesCustomer(customer, field)) {
          return
        }

        totalFields++
        
        if (isFieldFilled(field, formData)) {
          filledFields++
        } else {
          unfilledFields.push({
            stepName: step.name,
            fieldLabel: field.label,
            fieldId: field.id
          })
        }
      })
    })

    return {
      totalFields,
      filledFields,
      unfilledFields,
      completionPercentage: totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0
    }
  }

  const stats = calculateStats()

  return (
    <Box className={classes.container}>
      <Typography variant="h6" className={classes.summaryTitle}>
        Resumo do Preenchimento
      </Typography>
      
      <Box className={classes.statsContainer}>
        <Typography variant="body1" className={classes.statsText}>
          <strong>{stats.filledFields}</strong> de <strong>{stats.totalFields}</strong> campos preenchidos
        </Typography>
        
        <Typography variant="body2" className={classes.percentage}>
          ({stats.completionPercentage}% completo)
        </Typography>
      </Box>

      {stats.unfilledFields.length > 0 && (
        <Alert severity="warning" className={classes.alert}>
          <Typography variant="subtitle2" className={classes.alertTitle}>
            Campos não preenchidos:
          </Typography>
          <Box className={classes.unfilledList}>
            {stats.unfilledFields.map((field, index) => (
              <Typography key={index} variant="body2" className={classes.unfilledItem}>
                • <strong>{field.stepName}:</strong> {field.fieldLabel}
              </Typography>
            ))}
          </Box>
        </Alert>
      )}

      {stats.completionPercentage === 100 && (
        <Alert severity="success" className={classes.alert}>
          <Typography variant="body2">
            ✓ Todos os campos obrigatórios foram preenchidos!
          </Typography>
        </Alert>
      )}
    </Box>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  summaryTitle: {
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  statsContainer: {
    marginBottom: theme.spacing(2),
  },
  statsText: {
    marginBottom: theme.spacing(0.5),
  },
  percentage: {
    color: theme.palette.text.secondary,
  },
  alert: {
    marginTop: theme.spacing(1),
  },
  alertTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  unfilledList: {
    marginTop: theme.spacing(1),
  },
  unfilledItem: {
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
}))

ReviewSummary.propTypes = {
  steps: PropTypes.array.isRequired,
  formData: PropTypes.object.isRequired,
  customer: PropTypes.object,
}

export default ReviewSummary
